import { useEffect, useRef, useState, useMemo } from 'react'
import { DEPT_COLORS } from '../lib/deptColors'
import { registrationColor } from '../lib/constants'
import { useAuth } from '../hooks/useAuth'

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  })
}

export default function DayPickerPopover({ date, subtasks, onAddSubtask, onEditSubtask, onDeleteSubtask, onClose, anchorRect, groupBy = null }) {
// groupBy: null = flat list, 'registration' = dept calendar, 'department' = workflows
const grouped = groupBy === 'registration'
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const ref = useRef(null)
  const [expandedRegs, setExpandedRegs] = useState(() => {
    if (groupBy === 'department') {
      return new Set(subtasks.map(s => s.department).filter(Boolean))
    }
    return new Set()
  })

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const style = anchorRect ? {
    position: 'fixed',
    top: Math.min(anchorRect.bottom + 4, window.innerHeight - 300),
    left: Math.min(anchorRect.left, window.innerWidth - 280),
    zIndex: 9999,
    width: 272,
  } : {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
    width: 272,
  }

  const toggleReg = (reg) => {
    setExpandedRegs(prev => {
      const next = new Set(prev)
      next.has(reg) ? next.delete(reg) : next.add(reg)
      return next
    })
  }

  // Build grouped map for department view
  const groupedMap = subtasks.reduce((acc, s) => {
    const reg = s._registration || '—'
    if (!acc[reg]) acc[reg] = []
    acc[reg].push(s)
    return acc
  }, {})

  return (
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <div ref={ref} className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" style={style}>

        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Selected Day</div>
            <div className="text-sm font-semibold text-gray-900">{formatDate(date)}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Subtask list */}
        <div className="max-h-64 overflow-y-auto">
          {subtasks.length === 0 ? (
            <div className="px-4 py-4 text-xs text-gray-400 italic text-center">
              No subtasks on this day
            </div>
          ) : groupBy === 'department' ? (
  // Grouped by department — workflows view
  <div className="divide-y divide-gray-100">
    {Object.entries(
      subtasks.reduce((acc, s) => {
        const dept = s.department || '—'
        if (!acc[dept]) acc[dept] = []
        acc[dept].push(s)
        return acc
      }, {})
    ).map(([dept, deptSubtasks]) => {
      const isExpanded = expandedRegs.has(dept)
      const c = { hex: orgDeptMap[dept]?.hex || '#9ca3af' }
      return (
        <div key={dept}>
          <button
            onClick={() => toggleReg(dept)}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: c?.hex }} />
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${c?.bg} ${c?.text}`}>
              {dept}
            </span>
            <span className="text-xs text-gray-400 flex-1 text-left">{deptSubtasks.length} task{deptSubtasks.length !== 1 ? 's' : ''}</span>
            <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="bg-gray-50 border-t border-gray-100">
              {deptSubtasks.map(s => (
                <div key={s.id} className="flex items-center hover:bg-gray-100 transition-colors">
                  <button
                    onClick={() => onEditSubtask?.(s)}
                    className="flex-1 text-left px-4 py-2 pl-8 flex items-center gap-2"
                    disabled={!onEditSubtask}
                  >
                    <span className="text-xs text-gray-700 truncate">{s.task_name}</span>
                    {onEditSubtask && (
                      <svg className="w-3 h-3 text-gray-300 hover:text-brand-red flex-shrink-0 ml-auto transition-colors"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => onDeleteSubtask && onDeleteSubtask(s)}
                    className="px-3 py-2 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    })}
  </div>
) : grouped ? (
  // Grouped by registration — department view
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedMap).map(([reg, regSubtasks]) => {
                const isExpanded = expandedRegs.has(reg)
                const regColor = registrationColor(reg)
                return (
                  <div key={reg}>
                    <button
                      onClick={() => toggleReg(reg)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: regColor }} />
                      <span className="text-sm font-bold text-gray-800 flex-1 text-left">{reg}</span>
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
                        {regSubtasks.length}
                      </span>
                      <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-100">
                        {regSubtasks.map(s => {
                          const c = { hex: orgDeptMap[s.department]?.hex || '#9ca3af' }
                          if (s._isWorkscope) {
                            return (
                              <div key={s.id} className="px-4 py-2 pl-8 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-300" />
                                <span className="text-xs text-gray-400 italic truncate">{s.task_name}</span>
                              </div>
                            )
                          }
                          return (
                            <div key={s.id} className="flex items-center hover:bg-gray-100 transition-colors">
                              <button
                                onClick={() => onEditSubtask?.(s)}
                                className="flex-1 text-left px-4 py-2 pl-8 flex items-center gap-2"
                                disabled={!onEditSubtask}
                              >
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: c?.hex }} />
                                <span className="text-xs text-gray-700 truncate">{s.task_name}</span>
                                {onEditSubtask && (
                                  <svg className="w-3 h-3 text-gray-300 hover:text-brand-red flex-shrink-0 ml-auto transition-colors"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => onDeleteSubtask && onDeleteSubtask(s)}
                                className="px-3 py-2 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                title="Delete subtask"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            // Flat list — workflows view
            <div className="divide-y divide-gray-100">
              {subtasks.map(s => {
                const c = { hex: orgDeptMap[s.department]?.hex || '#9ca3af' }
                return (
                  <div key={s.id} className="flex items-center hover:bg-gray-50 transition-colors">
                    <button
                      onClick={() => onEditSubtask?.(s)}
                      className="flex-1 text-left px-4 py-2.5 flex items-center gap-3"
                      disabled={!onEditSubtask}
                    >
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c?.hex }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {s._registration && (
                            <span className="text-xs font-bold text-gray-600 flex-shrink-0">{s._registration}</span>
                          )}
                          <span className="text-sm font-medium text-gray-800 truncate">{s.task_name}</span>
                        </div>
                      </div>
                      {onEditSubtask && (
                        <svg className="w-3.5 h-3.5 text-gray-300 hover:text-brand-red flex-shrink-0 transition-colors"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => onDeleteSubtask && onDeleteSubtask(s)}
                      className="px-3 py-2.5 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                      title="Delete subtask"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Add subtask button */}
        <div className="px-4 py-3 border-t border-gray-100">
          <button
            onClick={onAddSubtask}
            className="w-full flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {groupBy === 'registration' ? 'Add Project' : 'Add Subtask'}
          </button>
        </div>

      </div>
    </>
  )
}